import React, { useLayoutEffect, useRef, type ReactNode } from 'react';
import type { SyntheticEvent } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Image from 'next/image';

export type WarningModalType = 'info' | 'warning' | 'error' | 'success';

interface WarningModalProps {
  open: boolean;
  onClose: () => void;
  type?: WarningModalType; // icon and color
  title?: string;
  message?: string;
  /** Structured body content; takes precedence over `message` when provided. */
  messageNode?: ReactNode;
  primaryButtonText?: string;
  primaryButtonTooltip?: string;
  onPrimary?: () => void;
  secondaryButtonText?: string;
  onSecondary?: () => void;
  secondaryButtonBackgroundColor?: string; // Background color for secondary button
  secondaryButtonBorderColor?: string; // Border color for secondary button
  secondaryButtonTextColor?: string; // Text color for secondary button
  secondaryButtonHoverBackgroundColor?: string; // Hover background color for secondary button
  secondaryButtonHoverBorderColor?: string; // Hover border color for secondary button
  secondaryButtonDisabledBackgroundColor?: string; // Disabled background color for secondary button
  secondaryButtonDisabledBorderColor?: string; // Disabled border color for secondary button
  secondaryButtonDisabledTextColor?: string; // Disabled text color for secondary button
  secondaryButtonPosition?: 'left' | 'right'; // Position of secondary button (default: 'right')
  tertiaryButtonText?: string;
  tertiaryButtonTooltip?: string;
  onTertiary?: () => void;
  tertiaryButtonBackgroundColor?: string;
  tertiaryButtonBorderColor?: string;
  tertiaryButtonTextColor?: string;
  tertiaryButtonHoverBackgroundColor?: string;
  tertiaryButtonHoverBorderColor?: string;
  quaternaryButtonText?: string;
  quaternaryButtonTooltip?: string;
  onQuaternary?: () => void;
  quaternaryButtonBackgroundColor?: string;
  quaternaryButtonBorderColor?: string;
  quaternaryButtonTextColor?: string;
  quaternaryButtonHoverBackgroundColor?: string;
  quaternaryButtonHoverBorderColor?: string;
  remainingTime?: string; // New prop for countdown display
  isCritical?: boolean; // New prop for critical state
  zIndex?: number; // Custom z-index for the modal
  primaryButtonDisabled?: boolean; // Disable primary button (e.g., during async operations)
  secondaryButtonDisabled?: boolean; // Disable secondary button independently
  tertiaryButtonDisabled?: boolean; // Disable tertiary button independently
  /** Outlined brand blue (#153C7F), same pattern as footer Save Progress */
  primaryButtonOutlinedBlue?: boolean;
  container?: HTMLElement | null; // Render dialog into this element (e.g. for stacking above other modals)
  /** When true, no extra dim overlay — use when stacking above an open modal. */
  hideBackdrop?: boolean;
  /** Dialog max width. Defaults to "xs". */
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
}

const ICONS: Record<WarningModalType, { src: string; alt: string; color: string }> = {
  info:    { src: '/warning/info.svg',    alt: 'Info',    color: '#153C7F' },
  warning: { src: '/warning/warning.svg', alt: 'Warning', color: '#B89B00' },
  error:   { src: '/warning/error.svg',   alt: 'Error',   color: '#F44336' },
  success: { src: '/warning/check.svg',   alt: 'Success', color: '#63A821' },
};

const DEFAULTS: Record<WarningModalType, { title: string; primary: string; }> = {
  info:    { title: 'Just So You Know', primary: 'OK' },
  warning: { title: 'Just So You Know', primary: 'OK' },
  error:   { title: 'Something Went Wrong', primary: 'Retry' },
  success: { title: 'Success', primary: 'OK' },
};

const WarningModal: React.FC<WarningModalProps> = ({
  open,
  onClose,
  type = 'info',
  title,
  message,
  messageNode,
  primaryButtonText,
  primaryButtonTooltip,
  onPrimary,
  secondaryButtonText,
  onSecondary,
  secondaryButtonBackgroundColor = '#F3F1FF',
  secondaryButtonBorderColor = '#F3F1FF',
  secondaryButtonTextColor = '#767C88',
  secondaryButtonHoverBackgroundColor = '#E8E5FF',
  secondaryButtonHoverBorderColor = '#E8E5FF',
  secondaryButtonDisabledBackgroundColor = '#F3F1FF',
  secondaryButtonDisabledBorderColor = '#F3F1FF',
  secondaryButtonDisabledTextColor = '#9CA3AF',
  secondaryButtonPosition = 'right',
  tertiaryButtonText,
  tertiaryButtonTooltip,
  onTertiary,
  tertiaryButtonBackgroundColor = '#fff',
  tertiaryButtonBorderColor = '#63A821',
  tertiaryButtonTextColor = '#63A821',
  tertiaryButtonHoverBackgroundColor = '#F0F9ED',
  tertiaryButtonHoverBorderColor = '#4e8c1a',
  quaternaryButtonText,
  quaternaryButtonTooltip,
  onQuaternary,
  quaternaryButtonBackgroundColor = '#fff',
  quaternaryButtonBorderColor = '#153C7F',
  quaternaryButtonTextColor = '#153C7F',
  quaternaryButtonHoverBackgroundColor = '#F3F1FF',
  quaternaryButtonHoverBorderColor = '#153C7F',
  secondaryButtonDisabled = false,
  tertiaryButtonDisabled = false,
  remainingTime,
  isCritical = false,
  zIndex,
  primaryButtonDisabled = false,
  primaryButtonOutlinedBlue = false,
  container,
  hideBackdrop = false,
  maxWidth = 'xs',
}) => {
  const icon = ICONS[type] || ICONS.info;
  const defaultTitle = title || DEFAULTS[type].title;
  const defaultPrimary = primaryButtonText || DEFAULTS[type].primary;
  const dialogRootRef = useRef<HTMLDivElement>(null);

  const primaryContainedSx = {
    backgroundColor: '#63A821',
    color: '#fff',
    borderRadius: '6px',
    fontWeight: 600,
    fontFamily: 'Poppins, sans-serif',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: '#4e8c1a',
      transform: 'translateY(-1px)',
    },
    '&:disabled': {
      backgroundColor: '#D1D5DB',
      color: '#9CA3AF',
      transform: 'none',
    },
  } as const;

  const primaryOutlinedBlueSx = {
    color: '#153C7F',
    borderColor: '#153C7F',
    backgroundColor: '#fff',
    borderRadius: '6px',
    fontWeight: 500,
    fontFamily: 'Poppins, sans-serif',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      borderColor: '#153C7F',
      backgroundColor: 'rgba(21, 60, 127, 0.04)',
      transform: 'translateY(-1px)',
    },
    '&:disabled': {
      backgroundColor: '#fff',
      color: '#9CA3AF',
      borderColor: '#D1D5DB',
      transform: 'none',
    },
  } as const;

  const tooltipSlotProps = {
    popper: {
      sx: { zIndex: zIndex != null ? zIndex + 2 : 99999 },
    },
  } as const;

  // Check if time is running low (less than 2 minutes)
  const isTimeRunningLow = remainingTime && remainingTime.startsWith('00:') && parseInt(remainingTime.split(':')[1]) < 2;

  // Determine colors based on critical state
  const getCountdownColors = () => {
    if (isCritical) {
      return {
        background: '#FFEBEE',
        border: '#F44336',
        text: '#F44336',
      };
    } else if (isTimeRunningLow) {
      return {
        background: '#FFF3CD',
        border: '#FF9800',
        text: '#FF9800',
      };
    } else {
      return {
        background: '#FFF3CD',
        border: '#B89B00',
        text: '#B89B00',
      };
    }
  };

  const colors = getCountdownColors();

  useLayoutEffect(() => {
    if (!open || zIndex == null) return;

    const applyStackZIndex = () => {
      const root = dialogRootRef.current;
      if (!root) return;
      // Elevate only the Modal root. Keep the backdrop at relative z-index -1
      // (MUI default) so it stays behind this dialog's paper — assigning an
      // absolute high z-index to the backdrop buries the content under itself.
      root.style.setProperty("z-index", String(zIndex), "important");
      const backdrop = root.querySelector(".MuiBackdrop-root") as HTMLElement | null;
      if (backdrop) {
        backdrop.style.setProperty("z-index", "-1", "important");
      }
    };

    applyStackZIndex();
    const rafId = requestAnimationFrame(applyStackZIndex);
    return () => cancelAnimationFrame(rafId);
  }, [open, zIndex]);

  const handleDialogClose = (event: SyntheticEvent, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick') {
      return;
    }
    onClose();
  };

  return (
    <Dialog
      ref={dialogRootRef}
      container={container ?? undefined}
      hideBackdrop={hideBackdrop}
      open={open}
      onClose={handleDialogClose}
      maxWidth={maxWidth}
      fullWidth
      disableScrollLock={hideBackdrop}
      sx={zIndex ? {
        zIndex: `${zIndex} !important`,
        '& .MuiDialog-container': {
          position: 'relative',
          zIndex: 1,
        },
      } : undefined}
      slotProps={{
        backdrop: {
          sx: {
            // Relative to the Modal root stacking context — never an absolute
            // app-level z-index, or the overlay covers this dialog's content.
            zIndex: -1,
          },
        },
      }}
    >
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 6,
          pb: 3,
          px: 4,
        }}
      >
        <div style={{
          background: '#fff',
          borderRadius: '50%',
          width: 120,
          height: 120,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 32,
          boxShadow: '0 2px 8px rgba(21, 60, 127, 0.08)',
        }}>
          <Image src={icon.src} alt={icon.alt} width={72} height={72} />
        </div>
        <DialogTitle
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            fontSize: 24,
            color: icon.color,
            p: 0,
            mb: 3,
            fontFamily: 'Poppins, sans-serif',
          }}
        >
          {defaultTitle}
        </DialogTitle>
        {remainingTime && (
          <div style={{
            background: colors.background,
            border: `2px solid ${colors.border}`,
            borderRadius: '12px',
            padding: '16px 24px',
            marginBottom: 16,
            textAlign: 'center',
            fontFamily: 'Poppins, sans-serif',
            animation: (isTimeRunningLow || isCritical) ? 'pulse 1s infinite' : 'none',
          }}>
            <div style={{
              fontSize: 14,
              color: colors.text,
              fontWeight: 600,
              marginBottom: 4,
            }}>
              {isCritical ? 'CRITICAL: Time Remaining' : 'Time Remaining'}
            </div>
            <div style={{
              fontSize: 32,
              fontWeight: 700,
              color: colors.text,
              fontFamily: 'monospace',
              letterSpacing: '2px',
            }}>
              {remainingTime}
            </div>
          </div>
        )}
        {messageNode ? (
          <Box
            sx={{
              width: '100%',
              textAlign: 'left',
              mb: 4,
              mt: 0,
            }}
          >
            {messageNode}
          </Box>
        ) : message ? (
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
              mb: 4,
              mt: 0,
            }}
          >
            {message.includes('<') ? (
              <Typography
                component="div"
                sx={{
                  color: '#4D4D4D',
                  fontSize: 16,
                  lineHeight: 1.6,
                  fontFamily: 'Poppins, sans-serif',
                  textAlign: 'center',
                }}
                dangerouslySetInnerHTML={{ __html: message }}
              />
            ) : (
              (() => {
                const paragraphs = message.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
                const baseSx = {
                  color: '#4D4D4D',
                  fontSize: 16,
                  lineHeight: 1.6,
                  fontFamily: 'Poppins, sans-serif',
                  textAlign: 'center' as const,
                  mb: 0,
                };
                return paragraphs.map((paragraph, i) => {
                  const isItemLine = paragraph.startsWith('Item: ');
                  const itemLabel = isItemLine ? 'Item: ' : null;
                  const itemValue = isItemLine ? paragraph.slice(6) : null;
                  return (
                    <Typography
                      key={i}
                      component="div"
                      sx={{
                        ...baseSx,
                        mb: i < paragraphs.length - 2 ? 2.5 : 0,
                      }}
                    >
                      {isItemLine && itemLabel != null && itemValue != null ? (
                        <>
                          {itemLabel}
                          <Box component="span" sx={{ fontWeight: 700 }}>{itemValue}</Box>
                        </>
                      ) : (
                        paragraph
                      )}
                    </Typography>
                  );
                });
              })()
            )}
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={{
        p: 3,
        pt: 2,
        justifyContent: secondaryButtonPosition === 'left' ? 'space-between' : 'flex-end',
        gap: 2,
      }}>
        {secondaryButtonPosition === 'left' && secondaryButtonText && (
          <Button
            type="button"
            onClick={onSecondary || onClose}
            disabled={secondaryButtonDisabled}
            sx={{
              backgroundColor: secondaryButtonBackgroundColor,
              color: secondaryButtonTextColor,
              border: `1px solid ${secondaryButtonBorderColor}`,
              borderRadius: '6px',
              fontWeight: 400,
              fontFamily: 'Poppins, sans-serif',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: secondaryButtonHoverBackgroundColor,
                borderColor: secondaryButtonHoverBorderColor,
                transform: 'translateY(-1px)',
              },
              '&:disabled': {
                backgroundColor: secondaryButtonDisabledBackgroundColor,
                color: secondaryButtonDisabledTextColor,
                borderColor: secondaryButtonDisabledBorderColor,
                transform: 'none',
              }
            }}
          >
            {secondaryButtonText}
          </Button>
        )}
        <div style={{ display: 'flex', gap: '16px', marginLeft: secondaryButtonPosition === 'left' ? 'auto' : 0 }}>
          {tertiaryButtonText && (
            tertiaryButtonTooltip ? (
              <Tooltip title={tertiaryButtonTooltip} arrow slotProps={tooltipSlotProps}>
                <span style={{ display: 'inline-flex' }}>
                  <Button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onTertiary?.();
                    }}
                    disabled={tertiaryButtonDisabled}
                    sx={{
                      backgroundColor: tertiaryButtonBackgroundColor,
                      color: tertiaryButtonTextColor,
                      border: `1px solid ${tertiaryButtonBorderColor}`,
                      borderRadius: '6px',
                      fontWeight: 400,
                      px: 2,
                      fontFamily: 'Poppins, sans-serif',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: tertiaryButtonHoverBackgroundColor,
                        borderColor: tertiaryButtonHoverBorderColor,
                        transform: 'translateY(-1px)',
                      },
                      '&:disabled': {
                        backgroundColor: '#fff',
                        color: '#9CA3AF',
                        borderColor: '#D1D5DB',
                        transform: 'none',
                      }
                    }}
                  >
                    {tertiaryButtonText}
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onTertiary?.();
                }}
                disabled={tertiaryButtonDisabled}
                sx={{
                  backgroundColor: tertiaryButtonBackgroundColor,
                  color: tertiaryButtonTextColor,
                  border: `1px solid ${tertiaryButtonBorderColor}`,
                  borderRadius: '6px',
                  fontWeight: 400,
                  px: 2,
                  fontFamily: 'Poppins, sans-serif',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: tertiaryButtonHoverBackgroundColor,
                    borderColor: tertiaryButtonHoverBorderColor,
                    transform: 'translateY(-1px)',
                  },
                  '&:disabled': {
                    backgroundColor: '#fff',
                    color: '#9CA3AF',
                    borderColor: '#D1D5DB',
                    transform: 'none',
                  }
                }}
              >
                {tertiaryButtonText}
              </Button>
            )
          )}
          {quaternaryButtonText && (
            quaternaryButtonTooltip ? (
              <Tooltip title={quaternaryButtonTooltip} arrow slotProps={tooltipSlotProps}>
                <span style={{ display: 'inline-flex' }}>
                  <Button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onQuaternary?.();
                    }}
                    disabled={primaryButtonDisabled}
                    sx={{
                      backgroundColor: quaternaryButtonBackgroundColor,
                      color: quaternaryButtonTextColor,
                      border: `1px solid ${quaternaryButtonBorderColor}`,
                      borderRadius: '6px',
                      fontWeight: 400,
                      px: 2,
                      fontFamily: 'Poppins, sans-serif',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: quaternaryButtonHoverBackgroundColor,
                        borderColor: quaternaryButtonHoverBorderColor,
                        transform: 'translateY(-1px)',
                      },
                      '&:disabled': {
                        backgroundColor: '#fff',
                        color: '#9CA3AF',
                        borderColor: '#D1D5DB',
                        transform: 'none',
                      }
                    }}
                  >
                    {quaternaryButtonText}
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onQuaternary?.();
                }}
                disabled={primaryButtonDisabled}
                sx={{
                  backgroundColor: quaternaryButtonBackgroundColor,
                  color: quaternaryButtonTextColor,
                  border: `1px solid ${quaternaryButtonBorderColor}`,
                  borderRadius: '6px',
                  fontWeight: 400,
                  px: 2,
                  fontFamily: 'Poppins, sans-serif',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: quaternaryButtonHoverBackgroundColor,
                    borderColor: quaternaryButtonHoverBorderColor,
                    transform: 'translateY(-1px)',
                  },
                  '&:disabled': {
                    backgroundColor: '#fff',
                    color: '#9CA3AF',
                    borderColor: '#D1D5DB',
                    transform: 'none',
                  }
                }}
              >
                {quaternaryButtonText}
              </Button>
            )
          )}
          {secondaryButtonPosition === 'right' && secondaryButtonText && (
            <Button
              type="button"
              onClick={onSecondary || onClose}
              disabled={secondaryButtonDisabled}
              sx={{
                backgroundColor: secondaryButtonBackgroundColor,
                color: secondaryButtonTextColor,
                border: `1px solid ${secondaryButtonBorderColor}`,
                borderRadius: '6px',
                fontWeight: 400,
                fontFamily: 'Poppins, sans-serif',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: secondaryButtonHoverBackgroundColor,
                  borderColor: secondaryButtonHoverBorderColor,
                  transform: 'translateY(-1px)',
                },
                '&:disabled': {
                  backgroundColor: secondaryButtonDisabledBackgroundColor,
                  color: secondaryButtonDisabledTextColor,
                  borderColor: secondaryButtonDisabledBorderColor,
                  transform: 'none',
                }
              }}
            >
              {secondaryButtonText}
            </Button>
          )}
          {primaryButtonTooltip ? (
            <Tooltip title={primaryButtonTooltip} arrow slotProps={tooltipSlotProps}>
              <span style={{ display: 'inline-flex' }}>
                <Button
                  type="button"
                  variant={primaryButtonOutlinedBlue ? 'outlined' : 'contained'}
                  onClick={onPrimary || onClose}
                  disabled={primaryButtonDisabled}
                  sx={primaryButtonOutlinedBlue ? primaryOutlinedBlueSx : primaryContainedSx}
                >
                  {defaultPrimary}
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button
              type="button"
              variant={primaryButtonOutlinedBlue ? 'outlined' : 'contained'}
              onClick={onPrimary || onClose}
              disabled={primaryButtonDisabled}
              sx={primaryButtonOutlinedBlue ? primaryOutlinedBlueSx : primaryContainedSx}
            >
              {defaultPrimary}
            </Button>
          )}
        </div>
      </DialogActions>
      <style jsx>{`
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.02); }
          100% { transform: scale(1); }
        }
      `}</style>
    </Dialog>
  );
};

export default WarningModal; 